import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CartPanel } from "@/components/CartPanel";
import { InspirationFeed } from "@/components/InspirationFeed";
import { useIsMobile } from "@/hooks/use-mobile";
import heroImage from "@/assets/hero-artisan.jpg";
import heroImageWebP from "@/assets/hero-artisan.webp";
import { Heart, Star, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useIsSeller } from "@/hooks/useIsSeller";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DiscountPrice } from "@/components/DiscountPrice";
import { isDiscountActive, type DiscountInfo } from "@/lib/discount-utils";
import { useQuery } from "@tanstack/react-query";
import { getDisplayPriceCents } from "@/lib/product-pricing";
import { CategoryCard } from "@/components/CategoryCard";
import { FeaturedArtisanCard } from "@/components/FeaturedArtisanCard";
import { optimizeSupabaseImage, getOriginalUrl, IMAGE_SIZES } from "@/utils/imageOptimization";
import { categories } from "@/data/categories";
import { categoryImages } from "@/data/categoryImages";
import { DigitalBadge } from "@/components/DigitalBadge";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
interface ProductItem {
  id: string;
  title: string;
  price_cents: number;
  currency: string | null;
  main_image_url: string | null;
  created_at?: string;
  discount_percentage?: number | null;
  discount_start_date?: string | null;
  discount_end_date?: string | null;
  discounted_price_cents?: number | null;
  view_count?: number | null;
  seller_id?: string;
  has_variants?: boolean;
  variants?: any;
  is_digital?: boolean;
}
const ProductCard = ({
  product
}: {
  product: ProductItem;
}) => {
  const navigate = useNavigate();
  const {
    toggleWishlist,
    isInWishlist
  } = useWishlist();
  const {
    toast
  } = useToast();
  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };
  const handleWishlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const wasInWishlist = isInWishlist(product.id);
    toggleWishlist(product.id);
    toast({
      title: wasInWishlist ? "Removed from wishlist" : "Added to wishlist",
      description: wasInWishlist ? "Item removed from your wishlist." : "Item added to your wishlist."
    });
  };
  const discountInfo: DiscountInfo = {
    discount_percentage: product.discount_percentage,
    discount_start_date: product.discount_start_date,
    discount_end_date: product.discount_end_date,
    discounted_price_cents: product.discounted_price_cents
  };
  return <Card className="group cursor-pointer perlla-transition hover:perlla-shadow-elegant" onClick={handleCardClick}>
      <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
        {product.is_digital && <DigitalBadge />}
        <img src={optimizeSupabaseImage(product.main_image_url, IMAGE_SIZES.productCard)} alt={product.title} width="400" height="300" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" onError={e => {
        const originalUrl = getOriginalUrl(product.main_image_url);
        if (e.currentTarget.src !== originalUrl && originalUrl !== '/placeholder.svg') {
          e.currentTarget.src = originalUrl;
        } else {
          e.currentTarget.src = '/placeholder.svg';
        }
      }} />
        {isDiscountActive(discountInfo) && product.discount_percentage && <div className="absolute top-3 left-3">
            <Badge variant="destructive">-{product.discount_percentage}%</Badge>
          </div>}
        <Button variant="ghost" size="icon" className="absolute top-3 right-3 bg-background/80 hover:bg-background transition-opacity" aria-label="Add to wishlist" onClick={handleWishlistClick}>
          <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? 'fill-primary text-primary' : ''}`} />
        </Button>
      </div>
    </Card>;
};
const Index = () => {
  const { t } = useTranslation(['homepage', 'common', 'categories']);
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const {
    items: cartItems,
    updateQuantity,
    removeItem
  } = useCart();
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const {
    isSeller
  } = useIsSeller();
  const sellerCta = isSeller ? {
    to: "/seller-dashboard",
    label: t('common:navigation.sellerDashboard')
  } : {
    to: "/become-a-seller",
    label: t('common:navigation.becomeSeller')
  };

  // Use static category images instead of fetching from products
  const categoriesWithImages = categories.map(category => ({
    id: category.id,
    name: t(`categories:categoryNames.${category.slug}`, category.name),
    slug: category.slug,
    image_url: categoryImages[category.id]
  }));

  // Fetch featured artisans
  const {
    data: featuredArtisans = []
  } = useQuery({
    queryKey: ["featured-artisans"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("sellers_public").select("id, shop_name, location, avatar_url").eq("is_featured", true).eq("onboarded", true).limit(4);
      if (error) throw error;
      return data || [];
    }
  });

  // Fetch new arrivals
  const {
    data: newArrivals = []
  } = useQuery({
    queryKey: ["new-arrivals"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("products").select("id, title, price_cents, main_image_url, currency, seller_id, discount_percentage, discount_start_date, discount_end_date, discounted_price_cents, created_at, has_variants, variants").eq("status", "published").order("created_at", {
        ascending: false
      }).limit(6);
      if (error) throw error;
      return data || [];
    }
  });

  // Fetch trending products (most views in last 7 days)
  const {
    data: trendingProducts = []
  } = useQuery({
    queryKey: ["trending-products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_trending_products", { days_back: 7, result_limit: 10 });
      if (error) throw error;
      return data || [];
    }
  });
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    // Add newsletter subscription logic here
    alert('Thank you for subscribing to our newsletter!');
  };
  return <div className="min-h-screen bg-background">
      {/* Top Bar - Desktop Only */}
      <div className="hidden md:block bg-primary text-primary-foreground py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex gap-4">
            <button 
              className={`hover:opacity-80 perlla-transition ${language === 'gr' ? 'font-bold underline' : ''}`}
              onClick={() => setLanguage('gr')}
            >
              GR
            </button>
            <span className="opacity-60">|</span>
            <button 
              className={`hover:opacity-80 perlla-transition ${language === 'en' ? 'font-bold underline' : ''}`}
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
          </div>
          <Link to={sellerCta.to} className="hover:opacity-80 perlla-transition">
            {sellerCta.label}
          </Link>
        </div>
      </div>
      

      {/* Desktop Content */}
      <div className="hidden md:block">
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          {!isMobile && <div className="absolute inset-0">
              <picture>
                <source srcSet={heroImageWebP} type="image/webp" />
                <img src={heroImage} alt="Cypriot artisan crafting traditional handmade products" width="1920" height="1080" fetchPriority="high" className="w-full h-full object-cover" />
              </picture>
              <div className="absolute inset-0 perlla-hero-gradient opacity-80"></div>
            </div>}
          
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-8xl font-display font-bold mb-6 animate-fade-in-up">
            {t('homepage:hero.title').split('.').map((part, i, arr) => 
              i === arr.length - 1 ? (
                <span key={i} className="text-secondary">{part.trim()}</span>
              ) : (
                <span key={i}>{part.trim()}.<br /></span>
              )
            )}
            </h1>
            <p className="text-xl md:text-2xl font-light mb-8 opacity-90 animate-fade-in-up">
              {t('homepage:hero.subtitle')}
            </p>
            <Button variant="hero" size="xl" className="animate-scale-in" onClick={() => navigate('/sales')}>
              {t('homepage:deals.viewAll')}
            </Button>
          </div>
        </section>

        {/* Shop Our Categories Section */}
        <section className="bg-gradient-to-b from-background to-background/50 py-[40px]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-display font-bold mb-6">{t('homepage:categories.title')}</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t('homepage:categories.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {categoriesWithImages.map(category => <CategoryCard key={category.id} id={category.id} name={category.name} slug={category.slug} image_url={category.image_url} />)}
            </div>
          </div>
        </section>

        {/* Featured Artisans Section */}
        {featuredArtisans.length > 0 && <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-display font-bold mb-6">{t('homepage:featuredArtisans.title')}</h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  {t('homepage:featuredArtisans.subtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {featuredArtisans.map(artisan => <FeaturedArtisanCard key={artisan.id} id={artisan.id} shopName={artisan.shop_name} location={artisan.location} avatarUrl={artisan.avatar_url} />)}
              </div>
            </div>
          </section>}

        {/* New Arrivals Section */}
        <section className="bg-gradient-to-b from-background/50 to-background py-[40px]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-display font-bold mb-6">{t('homepage:newArrivals.title')}</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t('homepage:newArrivals.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {newArrivals.map(product => <ProductCard key={product.id} product={product} />)}
            </div>
            <div className="text-center mt-12">
              <Button variant="outline" size="lg" asChild>
                <Link to="/new-arrivals">
                  {t('homepage:newArrivals.viewAll')} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Trending Now Section */}
        <section className="my-0 py-0">
          <div className="container mx-auto px-[16px] py-[15px] rounded-none">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-display font-bold mb-6">{t('homepage:trending.title')}</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t('homepage:trending.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
              {trendingProducts.map(product => <ProductCard key={product.id} product={product} />)}
            </div>
          </div>
        </section>

        {/* Newsletter - Desktop */}
        <section className="py-20 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-display font-bold mb-4">
              {t('homepage:newsletter.title')}
            </h2>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              {t('homepage:newsletter.subtitle')}
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col md:flex-row gap-4 max-w-md mx-auto">
              <Input placeholder={t('homepage:newsletter.emailPlaceholder')} className="bg-primary-foreground text-foreground flex-1" type="email" required />
              <Button variant="secondary" className="md:w-auto" type="submit">
                {t('homepage:newsletter.subscribe')}
              </Button>
            </form>
          </div>
        </section>
      </div>

      {/* Mobile Content */}
      <div className="block md:hidden">
        <InspirationFeed />
      </div>


      {/* Mobile Newsletter */}
      <div className="block md:hidden">
        <section className="py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl font-display font-bold mb-3">
              {t('homepage:newsletter.title')}
            </h2>
            <p className="text-sm opacity-90 mb-6">
              {t('homepage:newsletter.subtitle')}
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input placeholder={t('homepage:newsletter.emailPlaceholder')} className="bg-primary-foreground text-foreground text-sm" type="email" required />
              <Button variant="secondary" type="submit" size="sm">
                {t('homepage:newsletter.subscribe')}
              </Button>
            </form>
          </div>
        </section>
      </div>


      {/* Cart Panel */}
      <CartPanel isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} items={cartItems} onUpdateQuantity={updateQuantity} onRemoveItem={removeItem} />
    </div>;
};
export default Index;